import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { ProgressIndicator } from '@segmently/schema-models/v2';
import { mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import styles from './PageIndicator.module.css';

export interface PageIndicatorProps {
  indicator: ProgressIndicator | undefined;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
}

export const PageIndicator = memo(function PageIndicator({
  indicator,
  className,
  'data-testid': testId,
  editPath,
}: PageIndicatorProps): React.ReactElement | null {
  if (!indicator) {
    return null;
  }

  // Handle raw number (legacy) or missing value (setNestedValue corruption)
  const normalizedValue = typeof indicator === 'number'
    ? indicator
    : (indicator as any)?.value;
  const value = typeof normalizedValue === 'number' ? normalizedValue : 0;
  // v2.13.0: flat activeColor/trackColor with fallback to old lineAppearance for existing documents
  const ind = typeof indicator === 'number' ? undefined : (indicator as any);
  const resolvedTrackColor = ind?.trackColor ?? ind?.lineAppearance?.trackColor ?? ind?.appearance?.trackColor;
  const resolvedActiveColor = ind?.activeColor ?? ind?.lineAppearance?.color ?? ind?.appearance?.color;
  const lineLayout = typeof indicator === 'number' ? undefined : ((indicator as any).lineLayout ?? (indicator as any).layout);

  const layoutStyles: CSSProperties = mapFrameLayoutStyles(lineLayout);

  const progress = Math.max(0, Math.min(100, value ?? 0));

  const trackStyle: CSSProperties = {
    ...layoutStyles,
    ...(resolvedTrackColor && { backgroundColor: resolvedTrackColor }),
  };

  const barStyle: CSSProperties = {
    width: `${progress}%`,
    ...(resolvedActiveColor && { backgroundColor: resolvedActiveColor }),
  };

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  return (
    <div
      className={className ? `${styles.track} ${className}` : styles.track}
      style={trackStyle}
      data-testid={schemaTestId}
      data-edit-path={editPath}
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={styles.bar}
        style={barStyle}
        data-testid={schemaTestId ? `${schemaTestId}.bar` : undefined}
      />
    </div>
  );
});

PageIndicator.displayName = 'PageIndicator';
